import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { markPerformance } from "./performanceTrace";

export const LOCATION_OPTIONS = Object.freeze({
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 30000,
});

const isNative = () => Capacitor.isNativePlatform();

const normalizeNativeError = (error) => {
  const code = String(error?.code || "");
  if (code === "OS-PLUG-GLOC-0003") return "denied";
  if (code === "OS-PLUG-GLOC-0007") return "gps-off";
  if (code === "OS-PLUG-GLOC-0008") return "restricted";
  if (code === "OS-PLUG-GLOC-0010") return "timeout";
  return "unavailable";
};

const normalizeWebError = (error) => {
  if (error?.code === 1) return "denied";
  if (error?.code === 2) return "unavailable";
  if (error?.code === 3) return "timeout";
  return "unavailable";
};

const locationError = (reason, cause) => {
  const error = new Error(reason);
  error.code = `location/${reason}`;
  error.reason = reason;
  error.cause = cause;
  return error;
};

const toLocation = (position) => ({
  lat: position.coords.latitude,
  lng: position.coords.longitude,
  accuracy: position.coords.accuracy,
  speed: position.coords.speed,
  timestamp: position.timestamp,
});

export async function checkLocationPermission() {
  if (isNative()) {
    try {
      const status = await Geolocation.checkPermissions();
      return status.location || status.coarseLocation || "prompt";
    } catch (error) {
      return normalizeNativeError(error);
    }
  }
  if (!navigator.geolocation) return "unavailable";
  if (!navigator.permissions?.query) return "prompt";
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state || "prompt";
  } catch (_) {
    return "prompt";
  }
}

export async function requestLocationPermission() {
  if (!isNative()) return checkLocationPermission();
  try {
    const status = await Geolocation.requestPermissions({ permissions: ["location"] });
    return status.location || status.coarseLocation || "prompt";
  } catch (error) {
    return normalizeNativeError(error);
  }
}

export async function getCurrentLocation({ requestPermission = false, ...options } = {}) {
  const startedAt = performance.now();
  markPerformance("LOCATION_START", { requestPermission });
  const done = (result) => {
    markPerformance("LOCATION_DONE", { durationMs: Math.round(performance.now() - startedAt), status: "success" });
    return result;
  };
  let permission = await checkLocationPermission();
  if (requestPermission && (permission === "prompt" || permission === "prompt-with-rationale")) {
    permission = await requestLocationPermission();
  }
  if (["denied", "restricted", "gps-off", "unavailable"].includes(permission)) {
    throw locationError(permission);
  }

  if (isNative()) {
    try {
      return done(toLocation(await Geolocation.getCurrentPosition({ ...LOCATION_OPTIONS, ...options })));
    } catch (error) {
      throw locationError(normalizeNativeError(error), error);
    }
  }

  if (!navigator.geolocation) throw locationError("unavailable");
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(done(toLocation(position))),
      (error) => reject(locationError(normalizeWebError(error), error)),
      { ...LOCATION_OPTIONS, ...options },
    );
  });
}

export async function watchLocation(onPosition, onError, options = {}) {
  const permission = await checkLocationPermission();
  if (permission !== "granted") {
    onError?.(locationError(permission));
    return { stop: async () => {} };
  }

  if (isNative()) {
    const id = await Geolocation.watchPosition(
      { ...LOCATION_OPTIONS, ...options },
      (position, error) => {
        if (error) onError?.(locationError(normalizeNativeError(error), error));
        else if (position) onPosition(toLocation(position));
      },
    );
    return { stop: () => Geolocation.clearWatch({ id }) };
  }

  if (!navigator.geolocation) {
    onError?.(locationError("unavailable"));
    return { stop: async () => {} };
  }
  const id = navigator.geolocation.watchPosition(
    (position) => onPosition(toLocation(position)),
    (error) => onError?.(locationError(normalizeWebError(error), error)),
    { ...LOCATION_OPTIONS, ...options },
  );
  return { stop: async () => navigator.geolocation.clearWatch(id) };
}

export function persistLocationState(permission, position) {
  localStorage.setItem("locationPermission", permission);
  if (position) {
    localStorage.setItem("userLat", String(position.lat));
    localStorage.setItem("userLng", String(position.lng));
  }
}
